import { useEffect, useState } from "react";
import { School, Plus, Users, ClipboardList, Loader2, Sparkles, ClipboardCheck, Clock, ArrowRight } from "lucide-react";
import { listMyClasses, joinClass, listMyAssignments, startAssignment } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const JoinModal = ({ open, onClose, onJoined }) => {
  const [code, setCode] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!code.trim() || saving) return;
    setSaving(true);
    try {
      const r = await joinClass(code);
      toast.success(r.already_enrolled ? `Already in "${r.name}"` : `Joined "${r.name}" 🎓`);
      onJoined();
      onClose();
      setCode("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "That code isn't right");
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm p-0 overflow-hidden">
        <DialogTitle className="sr-only">Join a class</DialogTitle>
        <DialogDescription className="sr-only">Enter the 6-character code your instructor gave you to join their class.</DialogDescription>
        <form onSubmit={submit}>
          <div className="p-6">
            <h2 className="font-serif text-2xl font-bold">Join a class</h2>
            <p className="mt-1 text-sm text-muted-foreground">Enter the code your instructor gave you.</p>
            <input data-testid="join-class-code" value={code}
                   onChange={(e) => setCode(e.target.value.toUpperCase())}
                   placeholder="ABC123" maxLength={8} required
                   className="mt-5 w-full rounded-xl border-2 border-input bg-background px-4 py-4 text-center font-mono text-2xl tracking-widest uppercase outline-none focus:ring-2 focus:ring-ring/60" />
            <button data-testid="join-class-submit" type="submit" disabled={saving || !code.trim()}
                    className="mt-4 w-full rounded-xl bg-accent text-accent-foreground py-3.5 font-semibold disabled:opacity-60 hover:opacity-90 transition">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : "Join class"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const StatusPill = ({ status }) => {
  const cfg = {
    completed: { label: "✓ Done", cls: "bg-emerald-500/15 text-emerald-600" },
    failed: { label: "Didn't pass", cls: "bg-red-500/15 text-red-600" },
    in_progress: { label: "In progress", cls: "bg-amber-500/15 text-amber-600" },
    not_started: { label: "Not started", cls: "bg-muted/40 text-muted-foreground" },
  }[status] || { label: status, cls: "bg-muted/40 text-muted-foreground" };
  return <span className={`inline-block rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider ${cfg.cls}`}>{cfg.label}</span>;
};

export const StudentClasses = ({ onOpenAssignment }) => {
  const [classes, setClasses] = useState(null);
  const [selected, setSelected] = useState(null);
  const [assignments, setAssignments] = useState(null);
  const [joinOpen, setJoinOpen] = useState(false);
  const [starting, setStarting] = useState(null);

  const load = () => listMyClasses().then(setClasses).catch(() => setClasses([]));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!selected) return;
    listMyAssignments(selected.id).then(setAssignments).catch(() => setAssignments([]));
  }, [selected]);

  // Handle the `#join=CODE` deep-link from an instructor's copy-link button
  useEffect(() => {
    const m = window.location.hash.match(/join=([^&]+)/);
    if (m) {
      joinClass(m[1]).then((r) => {
        toast.success(r.already_enrolled ? `Already in "${r.name}"` : `Joined "${r.name}" 🎓`);
        window.history.replaceState(null, "", window.location.pathname);
        load();
      }).catch((e) => toast.error(e?.response?.data?.detail || "That code isn't right"));
    }
  }, []);

  const start = async (a) => {
    setStarting(a.id);
    try {
      const r = await startAssignment(a.id);
      onOpenAssignment(r.lecture_id, a);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start — try again");
    } finally {
      setStarting(null);
    }
  };

  if (classes === null) {
    return <div className="grid place-items-center py-16"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div>;
  }

  if (selected) {
    return (
      <div className="max-w-3xl mx-auto">
        <button onClick={() => { setSelected(null); setAssignments(null); }} className="mb-4 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition">
          ← All my classes
        </button>
        <h1 className="font-serif text-3xl font-bold">{selected.name}</h1>
        <p className="mt-1 text-sm text-muted-foreground">Instructor: {selected.instructor_name}</p>
        {assignments === null ? (
          <div className="grid place-items-center py-12"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div>
        ) : assignments.length === 0 ? (
          <p className="mt-8 text-sm text-muted-foreground text-center rounded-2xl border border-dashed border-border py-10">
            No assignments yet — your instructor will post some soon.
          </p>
        ) : (
          <ul className="mt-8 grid gap-3">
            {assignments.map((a) => (
              <li key={a.id} data-testid={`my-asg-${a.id}`} className="rounded-2xl border border-border bg-card p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-serif text-lg font-bold">{a.title}</p>
                    <p className="mt-0.5 text-xs text-muted-foreground truncate">
                      {a.mode === "shared" ? "Shared class lecture" : `${a.book_title}${a.chapters ? ` · ${a.chapters}` : ""}`}
                      {a.edition && ` · ${a.edition}`}
                    </p>
                    <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
                      <StatusPill status={a.my_status} />
                      {a.my_score != null && <span>· {Math.round(a.my_score)}%</span>}
                      {a.min_score > 0 && <span>· pass ≥ {Math.round(a.min_score)}%</span>}
                      {a.min_minutes > 0 && <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {a.min_minutes}m required</span>}
                    </div>
                    {a.due_at && <p className="mt-2 text-xs text-muted-foreground">Due {new Date(a.due_at).toLocaleString()}</p>}
                  </div>
                  <button data-testid={`start-asg-${a.id}`} onClick={() => start(a)} disabled={starting === a.id}
                          className="inline-flex items-center gap-1 rounded-full bg-accent text-accent-foreground px-4 py-2 text-sm font-semibold hover:opacity-90 transition disabled:opacity-60 shrink-0">
                    {starting === a.id ? <Loader2 className="h-4 w-4 animate-spin" /> :
                      a.my_status === "completed" ? <><ClipboardCheck className="h-4 w-4" /> Retake</> :
                      a.my_status === "in_progress" ? <>Continue <ArrowRight className="h-4 w-4" /></> :
                      <>Start <ArrowRight className="h-4 w-4" /></>}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl font-bold flex items-center gap-2"><School className="h-7 w-7 text-accent" /> My Classes</h1>
          <p className="mt-1 text-muted-foreground">All the classes you're enrolled in.</p>
        </div>
        <button data-testid="student-join-btn" onClick={() => setJoinOpen(true)}
                className="inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-5 py-2.5 font-semibold hover:opacity-90 transition">
          <Plus className="h-4 w-4" /> Join a class
        </button>
      </div>

      {classes.length === 0 ? (
        <div className="mt-8 rounded-3xl border border-dashed border-border p-12 text-center">
          <Sparkles className="h-8 w-8 text-accent mx-auto" />
          <p className="mt-4 font-serif text-xl">No classes yet</p>
          <p className="mt-1 text-sm text-muted-foreground">Got a class code from your instructor? Enter it here.</p>
          <button onClick={() => setJoinOpen(true)}
                  className="mt-6 inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-6 py-2.5 font-semibold hover:opacity-90 transition">
            <Plus className="h-4 w-4" /> Join a class
          </button>
        </div>
      ) : (
        <ul className="mt-8 grid gap-3 md:grid-cols-2">
          {classes.map((c) => (
            <li key={c.id} data-testid={`my-class-${c.id}`}>
              <button onClick={() => setSelected(c)}
                      className="w-full text-left rounded-2xl border border-border bg-card p-5 hover:border-accent/60 transition">
                <p className="font-serif text-xl font-bold">{c.name}</p>
                <p className="text-xs text-muted-foreground mt-0.5">Instructor: {c.instructor_name}</p>
                <div className="mt-4 flex items-center gap-3 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><ClipboardList className="h-3.5 w-3.5" /> {c.assignment_count}</span>
                  <span>· {c.completed_count}/{c.assignment_count} done</span>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}

      <JoinModal open={joinOpen} onClose={() => setJoinOpen(false)} onJoined={load} />
    </div>
  );
};
